import { useState } from 'react';
import { Auth } from 'aws-amplify';
import { Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';

/**
 * PasswordChange allows an authenticated external user to change their Cognito password.
 * Styled to match the login flow (ForgotPassword / NewPasswordSetup).
 */
const PasswordChange = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRequirements = [
    { label: 'At least 16 characters', test: (p) => p.length >= 16 },
  ];

  const allRequirementsMet = passwordRequirements.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const isSameAsCurrent = newPassword === currentPassword && newPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!allRequirementsMet) {
      setError('New password must be at least 16 characters.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }
    if (isSameAsCurrent) {
      setError('New password cannot be the same as current password.');
      return;
    }

    setLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, currentPassword, newPassword);
      setSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password error:', err);
      if (err.code === 'NotAuthorizedException') {
        setError('Current password is incorrect.');
      } else if (err.code === 'InvalidParameterException') {
        setError('New password does not meet the requirements.');
      } else if (err.code === 'LimitExceededException') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
      <Typography variant="overline" color="text.primary">Change Password</Typography>
      <Typography variant="body2" color="text.primary" style={{ opacity: 0.7 }}>
        Update your login password. You&apos;ll need your current password for verification.
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <TextField
          label="Current Password"
          type={showCurrentPassword ? 'text' : 'password'}
          variant="outlined"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          disabled={loading}
          InputProps={{
            endAdornment: (
              <Button
                onClick={() => setShowCurrentPassword((show) => !show)}
                size="small"
                tabIndex={-1}
              >
                {showCurrentPassword ? 'Hide' : 'Show'}
              </Button>
            ),
          }}
        />

        <TextField
          label="New Password"
          type={showNewPassword ? 'text' : 'password'}
          variant="outlined"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={loading}
          error={isSameAsCurrent}
          helperText={
            isSameAsCurrent
              ? 'New password cannot be the same as current password.'
              : newPassword.length > 0 && newPassword.length < 16
              ? `${newPassword.length}/16 characters`
              : ''
          }
          InputProps={{
            endAdornment: (
              <Button
                onClick={() => setShowNewPassword((show) => !show)}
                size="small"
                tabIndex={-1}
              >
                {showNewPassword ? 'Hide' : 'Show'}
              </Button>
            ),
          }}
        />

        <TextField
          label="Confirm New Password"
          type={showConfirmPassword ? 'text' : 'password'}
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={loading}
          error={confirmPassword.length > 0 && !passwordsMatch}
          helperText={
            confirmPassword.length > 0 && !passwordsMatch
              ? 'Passwords do not match'
              : ''
          }
          InputProps={{
            endAdornment: (
              <Button
                onClick={() => setShowConfirmPassword((show) => !show)}
                size="small"
                tabIndex={-1}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </Button>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading || !allRequirementsMet || !passwordsMatch || isSameAsCurrent}
          size="large"
        >
          {loading ? <CircularProgress size={24} /> : 'Change Password'}
        </Button>
      </form>
    </div>
  );
};

export default PasswordChange;
